/**
 * El perfil: la foto, todos los datos, y la contraseña.
 *
 * Tres formularios y no uno, porque son tres cosas con reglas distintas: la
 * foto se guarda apenas se elige (es lo único que hay que guardar en ese
 * bloque), los datos tienen un botón porque son quince campos y no se guarda
 * a mitad de camino, y la contraseña pide la actual y encima cierra la sesión.
 * Un solo «Guardar» para las tres haría que cambiar la foto pidiera la
 * contraseña.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ID_DOCUMENT_TYPES,
  SEXES,
  SUPPORTED_LOCALES,
  type PlayerProfile,
} from '@tennisup/contracts';
import { PROVINCES, cantonsOfProvince, districtsOfCanton } from '@tennisup/costa-rica-geo';

import { Alert } from '../components/Alert';
import { EmergencySection } from '../components/EmergencySection';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Avatar } from '../components/Avatar';
import { ChoiceGroup } from '../components/ChoiceGroup';
import { Select } from '../components/Select';
import { CountryPicker, type CountryOption } from '../components/CountryPicker';
import { Field } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { Plegable } from '../components/Plegable';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage, errorSummary } from '../lib/errorMessage';
import { countriesSortedFor, countryName, findCountry } from '../lib/countries';
import { changeLocale } from '../i18n';
import { useTheme } from '../theme/ThemeProvider';
import { toE164 } from './registerForm';

export function ProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const { session, setUser } = useAuth();
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api
      .myProfile()
      .then(setProfile)
      .catch((error: unknown) => setLoadError(errorMessage(t, error)));
  }, [t]);

  const user = session?.user;

  return (
    <AppLayout>
      {/* El perfil se abre desde «Más» y desde la foto de la barra de
          arriba. `navigate(-1)` devuelve por donde se vino; «Más» es el
          respaldo para quien llegó con el enlace directo. */}
      <BackButton to="/mas" />
      <h1>{t('profile.title')}</h1>

      {user !== undefined && (
        <div className="profile-head">
          <Avatar url={assetUrl(user.avatarPath)} name={user.displayName} size={96} />
          <div>
            <p className="profile-name">{user.displayName}</p>
            <p className="help">{user.email}</p>
          </div>
        </div>
      )}

      <PhotoSection />

      {loadError !== null && <Alert kind="error">{loadError}</Alert>}
      {profile === null ? (
        loadError === null && <p className="help">{t('common.loading')}</p>
      ) : (
        <DetailsSection
          profile={profile}
          onSaved={(saved) => {
            setProfile(saved);
            if (user !== undefined) {
              setUser({ ...user, displayName: saved.displayName, locale: saved.locale, theme: saved.theme });
            }
          }}
        />
      )}

      {/*
        LOS DATOS DE EMERGENCIA, DESPUÉS DE LOS DATOS Y ANTES DE LA CONTRASEÑA.

        Es el orden en que importan: quién soy, cómo me ayudan si me pasa
        algo, y al final lo de la cuenta. Formulario propio y no un bloque más
        del de arriba, por lo mismo que la foto y la contraseña — ver la
        cabecera de este archivo: un solo «Guardar» para todo haría que
        agregar un teléfono obligara a mandar los quince campos de la cédula.
      */}
      {profile !== null && (
        <EmergencySection profile={profile} onSaved={(guardado) => setProfile(guardado)} />
      )}

      {/*
        ACÁ IBA MI PROPIO HISTORIAL Y LO SACAMOS.

        Esta pantalla es un formulario —quién soy, dónde vivo, mi
        contraseña— y una tabla de cincuenta partidos con dos filtros y un
        paginador adentro de un formulario es otra pantalla metida a la
        fuerza. Sigue estando donde se lee de verdad: en la ficha de
        jugador, la misma que ve cualquiera.
      */}
      <PasswordSection />
    </AppLayout>
  );
}

// ---------------------------------------------------------------------
// Foto
// ---------------------------------------------------------------------

function PhotoSection(): JSX.Element {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);


  async function upload(file: File | null): Promise<void> {
    if (file === null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      setUser(await api.uploadPhoto(file));
      setSaved(true);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      {/*
        SIN `<h2>` ACÁ. Decía «Foto de perfil» y el campo de abajo se llama
        igual, así que el texto salía dos veces seguidas y se leía como un
        error de maquetación. Javier: «foto de perfil sale duplicado».

        LA QUE SE QUEDA ES LA ETIQUETA DEL CAMPO, no al revés: es lo que un
        lector de pantalla anuncia al llegar al input, y sin ella el campo
        queda sin nombre. Un título de sección con un solo campo debajo que ya
        se llama igual no agregaba nada.
      */}
      {error !== null && <Alert kind="error">{error}</Alert>}
      {saved && <Alert kind="success">{t('profile.photoSaved')}</Alert>}

      <ImagePicker
        id="profile-photo"
        label={t('profile.photo')}
        help={t('profile.photoHelp')}
        value={null}
        onChange={(file) => void upload(file)}
        disabled={busy}
      />

      {/*
        NO HAY BOTÓN DE QUITARLA. La foto es obligatoria al registrarse —que
        te reconozcan quienes juegan con vos—, y poder borrarla al minuto
        siguiente vaciaría esa regla. Se reemplaza eligiendo otra, que es lo
        que quiere quien la quiere cambiar.

        Las cuentas anteriores a la regla pueden no tener: no se les exige
        nada, pero tampoco se les ofrece borrar la que suban.
      */}
    </section>
  );
}

// ---------------------------------------------------------------------
// Datos
// ---------------------------------------------------------------------

/**
 * Lo que hay en pantalla. El teléfono se parte en país y número porque así se
 * edita, y se vuelve a juntar en E.164 al enviar — igual que en el registro,
 * y con la misma función.
 */
interface DetailsForm
  extends Omit<
    PlayerProfile,
    // La UBICACIÓN también queda fuera, y por la misma razón que la foto: no
    // se escribe en este formulario ni se guarda con su botón. Tenerla acá
    // significaría que guardar el perfil manda coordenadas viejas —o
    // ninguna— cada vez.
    | 'id'
    | 'email'
    | 'systemRole'
    | 'avatarPath'
    | 'whatsappPhone'
    | 'latitude'
    | 'longitude'
    | 'locationTakenAt'
    // El CLUB POR DEFECTO y el TEMA tampoco: los dos se eligen en «Más» y
    // tienen su propio camino. Acá dentro, guardar el perfil los mandaría de
    // vuelta con el valor que tenían cuando esta pantalla se abrió — o sea,
    // cambiar el tema en «Más» y después guardar el perfil lo revertiría.
    | 'defaultCommunityId'
    | 'theme'
    // Y LO DE «cómo me encuentran para jugar», por lo mismo: el nivel
    // declarado, si acepto retos y si quiero correos se eligen en «Más» y se
    // guardan por su propia ruta. Ver `SetPreferencesRequest`.
    | 'declaredLevel'
    | 'availableForChallenges'
    | 'hidden'
    | 'emailNotifications'
    // Y LOS DATOS DE EMERGENCIA, que tienen su propio formulario más abajo
    // en esta misma pantalla. Adentro de éste, guardar el nombre mandaría
    // el tipo de sangre y los contactos que estaban cargados cuando la
    // pantalla se abrió — o sea, pisaría lo que se acaba de guardar en el
    // otro bloque.
    | 'emergency'
  > {
  whatsappCountry: string;
  whatsappNumber: string;
}

/** Los datos están completos cuando se pueden guardar. Hoy: hace falta el sexo. */
function faltaCompletar(values: DetailsForm): boolean {
  return values.sex === null;
}

/**
 * De un E.164 guardado al par (país, número) que se edita.
 *
 * Se busca el prefijo MÁS LARGO que calce, no el primero: «+1» es Estados
 * Unidos y «+1876» es Jamaica, y quedarse con el primero que empieza igual
 * pondría a media Jamaica en Estados Unidos.
 */
function splitPhone(e164: string, countries: ReadonlyArray<{ code: string; dial: string }>): {
  country: string;
  number: string;
} {
  const digits = e164.replace(/\D/g, '');
  let mejor: { code: string; dial: string } | null = null;
  for (const country of countries) {
    if (digits.startsWith(country.dial) && (mejor === null || country.dial.length > mejor.dial.length)) {
      mejor = country;
    }
  }
  if (mejor === null) return { country: 'CR', number: digits };
  return { country: mejor.code, number: digits.slice(mejor.dial.length) };
}

function DetailsSection({
  profile,
  onSaved,
}: {
  profile: PlayerProfile;
  onSaved: (saved: PlayerProfile) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const countries = countriesSortedFor(i18n.language);

  /** Los países con su nombre traducido, para el buscador. */
  const paises = useMemo<CountryOption[]>(
    () =>
      countries.map((country) => ({
        code: country.code,
        name: countryName(country.code, i18n.language),
        flag: country.flag,
      })),
    [countries, i18n.language],
  );

  const [values, setValues] = useState<DetailsForm>(() => {
    const phone = splitPhone(profile.whatsappPhone, countries);
    return {
      displayName: profile.displayName,
      fullName: profile.fullName,
      idDocumentType: profile.idDocumentType,
      idDocumentNumber: profile.idDocumentNumber,
      dateOfBirth: profile.dateOfBirth,
      nationalityCode: profile.nationalityCode,
      sex: profile.sex,
      provinceCode: profile.provinceCode,
      cantonCode: profile.cantonCode,
      districtCode: profile.districtCode,
      addressLine: profile.addressLine,
      locale: profile.locale,
      whatsappCountry: phone.country,
      whatsappNumber: phone.number,
    };
  });

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const patch = (next: Partial<DetailsForm>): void => setValues((current) => ({ ...current, ...next }));
  const incompleto = faltaCompletar(values);

  const cantons = values.provinceCode === '' ? [] : cantonsOfProvince(values.provinceCode);
  const districts = values.cantonCode === '' ? [] : districtsOfCanton(values.cantonCode);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSending(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);
    try {
      if (values.sex === null) {
        // No puede pasar: el botón está apagado. Pero antes de mandar un
        // `null` donde el contrato dice que va un valor, mejor no mandar nada.
        setSending(false);
        return;
      }
      const address = (values.addressLine ?? '').trim();
      const guardado = await api.updateProfile({
        displayName: values.displayName.trim(),
        fullName: values.fullName.trim(),
        idDocumentType: values.idDocumentType,
        idDocumentNumber: values.idDocumentNumber.trim(),
        dateOfBirth: values.dateOfBirth,
        nationalityCode: values.nationalityCode,
        sex: values.sex,
        provinceCode: values.provinceCode,
        cantonCode: values.cantonCode,
        districtCode: values.districtCode,
        addressLine: address === '' ? null : address,
        whatsappPhone: toE164(values.whatsappCountry, values.whatsappNumber),
        locale: values.locale,
        // EL TEMA SALE DE LO QUE SE ESTÁ VIENDO, no del formulario. Se elige
        // en «Más» y no acá; mandar el que tenía esta pantalla al abrirse
        // revertiría el cambio que se acaba de hacer allá.
        theme,
      });
      onSaved(guardado);
      setSaved(true);
    } catch (caught) {
      // `errorSummary` y no `errorMessage`: los mensajes por campo se pintan
      // abajo, al lado de su input, y repetirlos acá arriba los dice dos
      // veces. Ver errorMessage.ts.
      setError(errorSummary(t, caught));
      const detalle = (caught as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (detalle !== undefined) setFieldErrors(detalle);
    } finally {
      setSending(false);
    }
  }

  return (
    <Plegable titulo={t('profile.details')} resumen={t('profile.detailsHint')} abierta={incompleto}>
      {/* Una cuenta anterior a que existiera la categoría llega sin ella. Se
          dice por qué importa, en vez de dejar un select en blanco que parece
          un detalle. */}
      {incompleto && <Alert kind="info">{t('profile.needsSex')}</Alert>}
      {error !== null && <Alert kind="error">{error}</Alert>}
      {saved && <Alert kind="success">{t('profile.saved')}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Subtitulo>{t('profile.groupWho')}</Subtitulo>
        <Field
          id="p-displayName"
          label={t('register.displayName')}
          help={t('register.displayNameHelp')}
          error={fieldErrors['displayName']}
        >
          {(control) => (
            <input {...control} value={values.displayName} onChange={(e) => patch({ displayName: e.target.value })} />
          )}
        </Field>

        <Field
          id="p-fullName"
          // Sin «Privado»: quien administra un club lo ve. Ver RegisterPage.
          label={t('register.fullName')}
          help={t('register.fullNameHelp')}
          error={fieldErrors['fullName']}
        >
          {(control) => (
            <input {...control} value={values.fullName} onChange={(e) => patch({ fullName: e.target.value })} />
          )}
        </Field>

        <div className="row">
          <Field id="p-idType" label={t('register.idDocumentType')}>
            {(control) => (
              <Select<PlayerProfile['idDocumentType']>
                {...control}
                value={values.idDocumentType}
                options={ID_DOCUMENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`idDocumentType.${type}`),
                }))}
                onChange={(idDocumentType) => patch({ idDocumentType })}
              />
            )}
          </Field>

          <Field
            id="p-idNumber"
            label={t('register.idDocumentNumber')}
            error={fieldErrors['idDocumentNumber']}
          >
            {(control) => (
              <input
                {...control}
                value={values.idDocumentNumber}
                onChange={(e) => patch({ idDocumentNumber: e.target.value })}
              />
            )}
          </Field>
        </div>

        <div className="row">
          {/* Sin opción «vacía»: una vez elegida no hay forma de volver a
              «sin categoría», y con radios eso sale gratis —simplemente no se
              puede desmarcar—. Mientras falta, no hay ninguno marcado, que se
              ve mucho más claro que un desplegable en blanco. */}
          <ChoiceGroup
            id="p-sex"
            legend={t('register.sex')}
            help={t('register.sexHelp')}
            value={values.sex}
            options={SEXES.map((sex) => ({ value: sex, label: t(`sex.${sex}`) }))}
            onChange={(sex) => patch({ sex })}
            {...(fieldErrors['sex'] === undefined ? {} : { error: fieldErrors['sex'] })}
          />

          <Field id="p-birth" label={t('register.dateOfBirth')} error={fieldErrors['dateOfBirth']}>
            {(control) => (
              <input
                {...control}
                type="date"
                value={values.dateOfBirth}
                onChange={(e) => patch({ dateOfBirth: e.target.value })}
              />
            )}
          </Field>
        </div>

        <CountryPicker
          id="p-nationality"
          label={t('register.nationality')}
          value={values.nationalityCode}
          options={paises}
          onChange={(nationalityCode) => patch({ nationalityCode })}
          {...(fieldErrors['nationalityCode'] === undefined
            ? {}
            : { error: fieldErrors['nationalityCode'] })}
        />

        <Subtitulo>{t('profile.groupWhere')}</Subtitulo>
        <Field id="p-province" label={t('register.province')} error={fieldErrors['provinceCode']}>
          {(control) => (
            <Select
              {...control}
              value={values.provinceCode}
              options={[
                { value: '', label: t('common.selectPlaceholder') },
                ...PROVINCES.map((province) => ({ value: province.code, label: province.name })),
              ]}
              // Cambiar de provincia invalida el cantón Y el distrito: un
              // distrito de San José colgando de Guanacaste no es un dato feo,
              // es un dato imposible.
              onChange={(provinceCode) => patch({ provinceCode, cantonCode: '', districtCode: '' })}
            />
          )}
        </Field>

        <div className="row">
          <Field id="p-canton" label={t('register.canton')} error={fieldErrors['cantonCode']}>
            {(control) => (
              <Select
                {...control}
                value={values.cantonCode}
                disabled={values.provinceCode === ''}
                options={[
                  { value: '', label: t('common.selectPlaceholder') },
                  ...cantons.map((canton) => ({ value: canton.code, label: canton.name })),
                ]}
                onChange={(cantonCode) => patch({ cantonCode, districtCode: '' })}
              />
            )}
          </Field>

          <Field id="p-district" label={t('register.district')} error={fieldErrors['districtCode']}>
            {(control) => (
              <Select
                {...control}
                value={values.districtCode}
                disabled={values.cantonCode === ''}
                options={[
                  { value: '', label: t('common.selectPlaceholder') },
                  ...districts.map((district) => ({ value: district.code, label: district.name })),
                ]}
                onChange={(districtCode) => patch({ districtCode })}
              />
            )}
          </Field>
        </div>

        <Field id="p-address" label={t('register.addressLine')} help={t('register.addressLineHelp')}>
          {(control) => (
            <input
              {...control}
              value={values.addressLine ?? ''}
              onChange={(e) => patch({ addressLine: e.target.value })}
            />
          )}
        </Field>

        <Subtitulo>{t('profile.groupContact')}</Subtitulo>
        <div className="phone">
          <Field id="p-phoneCountry" label={t('register.whatsappCountry')}>
            {(control) => (
              <Select
                {...control}
                value={values.whatsappCountry}
                options={countries.map((country) => ({
                  value: country.code,
                  label: `${country.flag} ${countryName(country.code, i18n.language)}`,
                  hint: `+${country.dial}`,
                }))}
                onChange={(whatsappCountry) => patch({ whatsappCountry })}
              />
            )}
          </Field>

          <Field
            id="p-phoneNumber"
            label={t('register.whatsappNumber')}
            help={t('register.whatsappExample', {
              country: countryName(values.whatsappCountry, i18n.language),
              example: findCountry(values.whatsappCountry)?.example ?? '8888 7777',
            })}
            error={fieldErrors['whatsappPhone']}
          >
            {(control) => (
              <input
                {...control}
                type="tel"
                inputMode="tel"
                value={values.whatsappNumber}
                onChange={(e) => patch({ whatsappNumber: e.target.value })}
              />
            )}
          </Field>
        </div>

        {/*
          ACÁ ESTABAN EL NIVEL DECLARADO, «disponible para retos», los avisos
          por correo Y LA UBICACIÓN, y los cuatro se fueron a «Más».

          No son quién es esta persona —eso es lo que queda acá: el nombre, el
          documento, dónde vive— sino CÓMO QUIERE QUE LA ENCUENTREN para
          jugar, y sobre eso uno cambia de opinión mucho más seguido que sobre
          el apellido. Lo pidió Javier.

          Y ADEMÁS YA NO PUEDEN PISARSE: se guardan por su propia ruta, así
          que guardar este formulario con un valor viejo cargado no borra lo
          que se acaba de elegir en la otra pantalla. Es la misma razón por la
          que el tema y el club por defecto nunca estuvieron acá.
        */}

        <Subtitulo>{t('profile.groupApp')}</Subtitulo>
        <div className="row">
          <Field id="p-locale" label={t('register.locale')}>
            {(control) => (
              <Select
                {...control}
                value={values.locale}
                options={SUPPORTED_LOCALES.map((locale) => ({
                  value: locale,
                  label: t(`language.${locale}`),
                }))}
                onChange={(locale) => {
                  patch({ locale });
                  // Se aplica en el acto, antes de guardar: cambiar el idioma
                  // y que la pantalla siga en el anterior hasta apretar un
                  // botón se siente roto.
                  void changeLocale(locale);
                }}
              />
            )}
          </Field>

        </div>

        <button type="submit" disabled={sending || incompleto}>
          {sending ? t('common.sending') : t('profile.save')}
        </button>
      </form>
    </Plegable>
  );
}

// ---------------------------------------------------------------------
// Contraseña
// ---------------------------------------------------------------------

function PasswordSection(): JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mismatch = confirm !== '' && confirm !== next;
  const canSubmit = current !== '' && next !== '' && confirm === next && !sending;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setSending(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.changePassword({ currentPassword: current, newPassword: next });
      // Cambiar la contraseña invalida TODAS las sesiones, incluida esta. Se
      // cierra acá y se manda a entrar de nuevo, en vez de dejar una pantalla
      // que va a fallar en la siguiente llamada sin decir por qué.
      signOut();
      navigate('/login', { replace: true, state: { passwordChanged: true } });
    } catch (caught) {
      setError(errorMessage(t, caught));
      const detalle = (caught as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (detalle !== undefined) setFieldErrors(detalle);
    } finally {
      setSending(false);
    }
  }

  return (
    <Plegable titulo={t('profile.passwordTitle')} resumen={t('profile.passwordHint')}>
      <p className="help">{t('profile.passwordHelp')}</p>
      {error !== null && <Alert kind="error">{error}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          id="pw-current"
          label={t('profile.currentPassword')}
          error={fieldErrors['currentPassword']}
        >
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="pw-new"
          label={t('profile.newPassword')}
          help={t('register.passwordHelp')}
          error={fieldErrors['newPassword']}
        >
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="pw-confirm"
          label={t('reset.passwordConfirm')}
          error={mismatch ? t('validation.passwordMismatch') : undefined}
        >
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>

        <button type="submit" disabled={!canSubmit}>
          {sending ? t('common.sending') : t('profile.changePassword')}
        </button>
      </form>
    </Plegable>
  );
}

/**
 * EL NOMBRE DE UN GRUPO DE CAMPOS: «Quién sos», «Dónde vivís»…
 *
 * «Tus datos» eran quince campos seguidos, y leídos así cuesta saber dónde
 * termina el documento y empieza la dirección. Con un nombre cada cuatro o
 * cinco se leen como cuatro bloques. Chico y en gris a propósito: ordena sin
 * competir con las etiquetas, que son lo que se lee.
 */
function Subtitulo({ children }: { children: string }): JSX.Element {
  return (
    <h3 className="mt-6 mb-3 text-xs font-semibold tracking-wider text-muted uppercase">
      {children}
    </h3>
  );
}
